import React from 'react'
import type { UseFormRegister, FieldValues, FieldErrors, Control } from 'react-hook-form'
import { dollarsToScientific, Input, Select } from '@climate/components'
import { countries, formsOfOrganization, industryClassifications, organizationSizeTypes, utilityCompanyTypes, energyTraderTypes } from '../../consts'
import type { CreditRatingAgency, CreditRatingsAgenciesGrouped, Entity } from '../../types'
import IndustryCombobox from './IndustryCombobox'

interface OrganizationDetailsSectionProps {
  isDraft: boolean
  entity: Entity
  jurisdictionCountry: string
  creditRatingsAgenciesGrouped: CreditRatingsAgenciesGrouped
  entityCreditRating: CreditRatingAgency | undefined
  register: UseFormRegister<FieldValues>
  control: Control<FieldValues>
  errors: FieldErrors
  userTitle?: string
  userRoleLabel?: string
}

export default function OrganizationDetailsSection({
  isDraft,
  entity,
  jurisdictionCountry,
  creditRatingsAgenciesGrouped,
  entityCreditRating,
  register,
  control,
  errors,
  userTitle,
  userRoleLabel,
}: OrganizationDetailsSectionProps) {
  const jurisdictionOptions = countries[jurisdictionCountry] || {}

  return (
    <>
      <h3 className="text-lg leading-6 font-medium text-gray-900 pb-4 border-b border-gray-300">
        Organization details
      </h3>

      <div className="grid sm:gap-x-8 gap-y-4 grid-cols-1 sm:grid-cols-12 pt-4">
        <div className="sm:col-span-4">
          <label htmlFor="form_of_organization" className="pb-2 block text-sm text-gray-700">
            Form of organization
          </label>
          {isDraft
            ? <Select
              id="form_of_organization"
              name="form_of_organization"
              options={formsOfOrganization}
              register={register('form_of_organization', { required: 'Required' })}
              errors={errors}
              placeholder=""
              className="mr-4"
            />
            : <span>{entity.form_of_organization}</span>
          }
        </div>

        <div className="sm:col-span-4">
          <label htmlFor="jurisdiction_country" className="pb-2 block text-sm text-gray-700">
            Country of incorporation
          </label>
          {isDraft
            ? <Select
              id="jurisdiction_country"
              name="jurisdiction_country"
              options={Object.keys(countries)}
              register={register('jurisdiction_country', { required: 'Required' })}
              errors={errors}
              placeholder=""
              className="mr-4"
            />
            : <span>{entity.jurisdiction_country}</span>
          }
        </div>

        <div className="sm:col-span-4">
          <label htmlFor="jurisdiction_of_organization" className="pb-2 block text-sm text-gray-700">
            State / Province of incorporation
          </label>
          {isDraft
            ? <Select
              id="jurisdiction_of_organization"
              name="jurisdiction_of_organization"
              disabled={!jurisdictionCountry}
              options={jurisdictionOptions}
              register={register('jurisdiction_of_organization', { required: 'Required' })}
              errors={errors}
              placeholder=""
              className="mr-4"
            />
            : <span>{entity.jurisdiction_of_organization}</span>
          }
        </div>

        <div className="sm:col-span-4">
          <label htmlFor="jurisdiction_of_organization_identifier" className="pb-2 block text-sm text-gray-700">
            Jurisdiction ID
          </label>
          {isDraft
            ? <Input
              id="jurisdiction_of_organization_identifier"
              name="jurisdiction_of_organization_identifier"
              register={register('jurisdiction_of_organization_identifier', { required: 'Required' })}
              errors={errors}
              autoComplete="jurisdiction_of_organization_identifier"
            />
            : <span>{entity.jurisdiction_of_organization_identifier}</span>
          }
        </div>

        <div className="sm:col-span-4">
          <label htmlFor="industry_classification_code" className="pb-2 block text-sm text-gray-700">
            Industry
          </label>
          {isDraft
            ? <IndustryCombobox
              name="industry_classification_code"
              control={control}
              errors={errors}
            />
            : <span>{industryClassifications[entity.industry_classification_code ?? ''] ?? entity.industry_classification_code}</span>
          }
        </div>

        <div className="sm:col-span-4">
          <label htmlFor="is_utility_company" className="pb-2 block text-sm text-gray-700">
            Is utility company?
          </label>
          {isDraft
            ? <Select
              id="is_utility_company"
              name="is_utility_company"
              options={utilityCompanyTypes}
              register={register('is_utility_company', { required: 'Required' })}
              errors={errors}
              placeholder=""
              className="mr-4"
            />
            : <span>{entity.is_utility_company}</span>
          }
        </div>

        <div className="sm:col-span-4">
          <label htmlFor="is_energy_trader" className="pb-2 block text-sm text-gray-700">
            Is energy trader?
          </label>
          {isDraft
            ? <Select
              id="is_energy_trader"
              name="is_energy_trader"
              options={energyTraderTypes}
              register={register('is_energy_trader', { required: 'Required' })}
              errors={errors}
              placeholder=""
              className="mr-4"
            />
            : <span>{entity.is_energy_trader}</span>
          }
        </div>

        <div className="sm:col-span-4">
          <label htmlFor="size_type" className="pb-2 block text-sm text-gray-700">
            Organization size of
          </label>
          {isDraft
            ? <Select
              id="size_type"
              name="size_type"
              options={organizationSizeTypes}
              register={register('size_type', { required: 'Required' })}
              errors={errors}
              placeholder=""
              className="mr-4"
            />
            : <span>{entity.size_type}</span>
          }
        </div>

        <div className="sm:col-span-4">
          <label htmlFor="size_dollars" className="pb-2 block text-sm text-gray-700">
            Organization size
          </label>
          {isDraft
            ? <Input
              type="number"
              id="size_dollars"
              name="size_dollars"
              register={register('size_dollars', { required: 'Required' })}
              errors={errors}
              postfix="$"
              className="pr-20"
            />
            : <span>{dollarsToScientific(entity.size_dollars)}</span>
          }
        </div>

        <div className="sm:col-span-6">
          <label htmlFor="agency_credit_rating_id" className="pb-2 block text-sm text-gray-700">
            Credit rating
          </label>
          {isDraft
            ? <Select
              id="agency_credit_rating_id"
              name="agency_credit_rating_id"
              groupedOptions={creditRatingsAgenciesGrouped as Record<string, { value: string; label: string }[]>}
              register={register('agency_credit_rating_id', { required: 'Required' })}
              errors={errors}
              placeholder=""
              className="mr-4"
            />
            : <span>{entityCreditRating?.agency} {entityCreditRating?.rating}</span>
          }
        </div>

        <div className="sm:col-span-6">
          <label htmlFor="agency_credit_rating_date" className="pb-2 block text-sm text-gray-700">
            Credit rating date
          </label>
          {isDraft
            ? <Input
              id="agency_credit_rating_date"
              type="date"
              name="agency_credit_rating_date"
              register={register('agency_credit_rating_date', { required: 'Required' })}
              errors={errors}
            />
            : <span>{entity.agency_credit_rating_date}</span>
          }
        </div>

        <div className="sm:col-span-4">
          <label htmlFor="user_role" className="pb-2 block text-sm text-gray-700">
            Desired Role
          </label>
          {isDraft
            ? <Select
              id="user_role"
              name="user_role"
              options={{ Buyer: 'Buying', Seller: 'Selling' }}
              register={register('user_role', { required: 'Required' })}
              errors={errors}
              placeholder=""
              className="mr-4"
            />
            : <span>{userRoleLabel}</span>
          }
        </div>

        <div className="sm:col-span-4">
          <label htmlFor="user_title" className="pb-2 block text-sm text-gray-700">
            Title
          </label>
          {isDraft
            ? <Input
              id="user_title"
              name="user_title"
              register={register('user_title', { required: 'Required' })}
              errors={errors}
              autoComplete="organization-title"
            />
            : <span>{userTitle}</span>
          }
        </div>
      </div>
    </>
  )
}
